import { z } from 'zod';
import { toContactPhone } from '../phone/kenyan-mobile';
import { defineListQuery, listFilter } from './list-query.schemas';

/**
 * Garages and parts suppliers (PRD → Maintenance → What a work order records, Parts;
 * gitaumoses4/chukua#173): reference data an admin manages. A garage's service types and a
 * supplier's categories are a managed catalogue (`directory_tags`) that a save can add to on
 * the fly. A garage's place is a zone (any kind, gitaumoses4/chukua#421), since a truck has to be able
 * to drive there; a supplier never gets one, since nothing drives there — only `location`, free text.
 * A later mechanic/supplier picker on work orders, quotes and parts lines reads this, but nothing
 * calls it yet.
 */

const name = z.string().trim().min(1).max(120);
const location = z.string().trim().min(1).max(200);

/** Null is accepted as well as absent and empty: the edit form is seeded from the stored record, whose empty notes, phone and email are null. */
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((value) => value || null);

/** A garage's or supplier's number may be a landline, so it stays free text; a Kenyan mobile is stored in E.164. */
const phone = optionalText(40).transform((value) => (value === null ? null : toContactPhone(value)));

const email = optionalText(254).refine((value) => value === null || z.email().safeParse(value).success, { message: 'Enter a valid email address' });

/**
 * The catalogue entries a record carries, each an existing code or a new name to add on the fly. Trimmed and
 * de-duplicated ignoring case, so "Tyres" and "tyres" are one tag; the first spelling is kept, as a new entry's
 * display name. At least one, so every record is filterable.
 */
const tags = (label: string) =>
  z
    .array(z.string().trim().min(1).max(60))
    .min(1, `Give at least one ${label}`)
    .max(20)
    .transform((values) => values.filter((value, index) => values.findIndex((other) => other.toLowerCase() === value.toLowerCase()) === index));

/** A zone's `code` (any kind): where the garage is, and what a truck is driven to. */
const zoneCode = z.string().trim().min(1).max(32);

export const garageInputSchema = z.object({
  name,
  zoneCode,
  phone,
  email,
  serviceTypes: tags('service type'),
  certifications: optionalText(2000),
});
export type GarageInput = z.output<typeof garageInputSchema>;

export const supplierInputSchema = z.object({
  name,
  location,
  phone,
  email,
  categories: tags('category'),
  specialities: optionalText(2000),
});
export type SupplierInput = z.output<typeof supplierInputSchema>;

export const DIRECTORY_STATUSES = ['active', 'inactive'] as const;
export type DirectoryStatus = (typeof DIRECTORY_STATUSES)[number];

/** `GET /garages` — search matches name and the zone's name or code; `place` sorts by the zone's name. Tag filters are lowercased because stored codes are. */
export const garageListQuerySchema = defineListQuery({
  sortFields: ['name', 'place'],
  defaultSort: { sort: 'name', order: 'asc' },
  filters: {
    serviceType: listFilter(z.string().trim().toLowerCase()),
    status: listFilter(z.enum(DIRECTORY_STATUSES)),
  },
});
export type GarageListQuery = z.infer<typeof garageListQuerySchema>;

/** `GET /suppliers` — search matches name and location. */
export const supplierListQuerySchema = defineListQuery({
  sortFields: ['name', 'location'],
  defaultSort: { sort: 'name', order: 'asc' },
  filters: {
    category: listFilter(z.string().trim().toLowerCase()),
    status: listFilter(z.enum(DIRECTORY_STATUSES)),
  },
});
export type SupplierListQuery = z.infer<typeof supplierListQuerySchema>;

export interface GarageView {
  id: string;
  name: string;
  /** The zone the garage is in (`zones.code`). Null only for a garage the previous version created, until someone picks a zone. */
  zoneCode: string | null;
  /** Null only if the zone row has gone missing; zones are deactivated, never deleted. */
  zoneName: string | null;
  phone: string | null;
  email: string | null;
  /** Catalogue codes (`directory_tags`). */
  serviceTypes: string[];
  certifications: string | null;
  isActive: boolean;
}

export interface SupplierView {
  id: string;
  name: string;
  location: string;
  phone: string | null;
  email: string | null;
  /** Catalogue codes (`directory_tags`). */
  categories: string[];
  specialities: string | null;
  isActive: boolean;
}

/** `GaragesService.searchActive` / `SuppliersService.searchActive` — a later picker's option shape. */
export interface DirectoryPickerOption {
  id: string;
  name: string;
  location: string;
}

export const DIRECTORY_TAG_KINDS = ['service_type', 'supplier_category'] as const;
export type DirectoryTagKind = (typeof DIRECTORY_TAG_KINDS)[number];

/** `POST /directory-tags` — adds an entry to a catalogue; its code is the lowercased name. */
export const directoryTagInputSchema = z.object({
  kind: z.enum(DIRECTORY_TAG_KINDS),
  name: z.string().trim().min(1).max(60),
});
export type DirectoryTagInput = z.output<typeof directoryTagInputSchema>;

/** `PATCH /directory-tags/:id` — the display name only; the code is what records store, so it never changes. */
export const directoryTagRenameSchema = directoryTagInputSchema.pick({ name: true });
export type DirectoryTagRename = z.output<typeof directoryTagRenameSchema>;

/** `GET /directory-tags` — one small catalogue per kind, unpaged like the zone kinds. */
export const directoryTagListQuerySchema = z.object({ kind: z.enum(DIRECTORY_TAG_KINDS) });
export type DirectoryTagListQuery = z.output<typeof directoryTagListQuerySchema>;

export interface DirectoryTagView {
  id: string;
  kind: DirectoryTagKind;
  code: string;
  name: string;
  isActive: boolean;
}
